import type { FieldMetaData } from "./fieldsMetaData";
import { createFieldMetaData } from "./createFieldMetaData";
import { ShowDates } from "../showDateUtils";
import { DoorsFormState } from "./companyFields";
import moment from "moment";
import { parseFromTime } from "../momentutils";

export interface ShowFormState {
  client: server.dto.ClientSmall
  tour: server.dto.TourSmall
  showDates: ShowDates
  status: server.dto.StatusSmall
  eventName: string
  eventWebsite: string
  publicationStatus: server.dto.PublicationStatuses
  costCenter: server.dto.CostCenter
  projectCode: string
  tags: server.dto.TagSmall[]
  owner: server.dto.UserSmall
  artist: server.dto.ArtistSmall
  attendees: server.dto.ContactSmallV2[]
  venue: server.dto.VenueSmall
  capacity: number
  ageRestriction: server.dto.AgeRestrictions
  doors: DoorsFormState
  venueContact: server.dto.ContactSmallV2
  promoter: server.dto.CompanySmall
  promoterContact: server.dto.ContactSmallV2
  hotel: server.dto.CompanySmall
  hotelContact: server.dto.ContactSmallV2
  onSaleDate: moment.Moment | null
  soldOut: boolean
  ticketLink: string
  presalePrice: number | null
  doorPrice: number | null
  reducedPrice: number | null
  bookkeeping: server.dto.BookkeepingSmall
  currency: server.dto.Currencies
  exchangeRate: number
  freefieldValues: server.dto.FreeFieldValue[]
};

export interface ExternalShowFormState {
  client: server.dto.ClientSmall
  tour: server.dto.TourSmall
  showDates: ShowDates
  status: server.dto.StatusSmall
  eventName: string
  eventWebsite: string
  artist: server.dto.ArtistSmall
  freefieldValues: server.dto.FreeFieldValue[]
}

const getTicketPrice = (show: server.dto.Show, type: server.dto.TicketTypes) => 
    show.tickets?.tickets?.find(t => t.type.value === type)?.price ?? null;

export const mapAppointmentDateTimeToShowDates = (appointmentDateTime: server.dto.AppointmentDateTime): ShowDates => ({
  startDisplayDate: moment.utc(appointmentDateTime.startDisplay),
  endDisplayDate: moment.utc(appointmentDateTime.endDisplay),
  mode: appointmentDateTime.mode,
  timeOfPerformance: appointmentDateTime.customFallbackTime
});

export const mapShowToShowFormState = (show: server.dto.Show): ShowFormState => ({

  //TODO: This mapping needs to be better, already caused bugs twice, because unnoticed some property was not mapped.
  //We need to make sure that every property in FormState is mapped
  //Also ts-ignore smells

  client: show.client,

  // @ts-ignore
  tour: show.tour,
  showDates: mapAppointmentDateTimeToShowDates(show.dateTime),
  status: show.status,
  // @ts-ignore
  eventName: show.eventName,
  eventWebsite: show.eventWebsite ?? "",
  publicationStatus: show.publicationStatus,
  // @ts-ignore
  costCenter: show.costCenter,
  // @ts-ignore
  projectCode: show.projectCode,
  tags: show.tags,
  owner: show.owner,
  // @ts-ignore
  artist: show.artist,
  attendees: show.attendees,
  // @ts-ignore
  venue: show.venue,
  // @ts-ignore
  venueContact: show.venueContact,
  capacity: show.capacity,
  ageRestriction: show.ageRestriction,
  doors: {
    // @ts-ignore
    open: show.doors?.open ? parseFromTime(show.doors.open) : null,
    // @ts-ignore
    close: show.doors?.close ? parseFromTime(show.doors.close) : null,
  },
  // @ts-ignore
  promoter: show.promoter,
  // @ts-ignore
  promoterContact: show.promoterContact,
  // @ts-ignore
  hotel: show.hotel,
  // @ts-ignore
  hotelContact: show.hotelContact,
  onSaleDate: show.tickets?.onSaleDate ? moment.utc(show.tickets.onSaleDate) : null,
  soldOut: show.tickets?.soldOut ?? false,
  ticketLink: show.tickets?.ticketLink?.value ?? "",

  presalePrice: getTicketPrice(show, "presale"),
  doorPrice: getTicketPrice(show, "door"),
  reducedPrice: getTicketPrice(show, "reduced"),

  bookkeeping: show.bookkeeping,
  currency: show.currency,
  exchangeRate: show.exchangeRate,

  freefieldValues: show.freeFields
})

export const mapShowFormStateToInsertShowInternalInstruction = (
  showFormState: ShowFormState,
  production: server.dto.TourSmall,
): server.dto.InsertShowInternalEnvironmentInstruction => ({
  dateTime: {
    startDisplay: showFormState.showDates.startDisplayDate.toDate(),
    endDisplay: showFormState.showDates.endDisplayDate.toDate(),
    customFallbackTime: showFormState.showDates.timeOfPerformance,
    mode: showFormState.showDates.mode
  },
  statusId: showFormState.status.statusId,
  eventName: showFormState.eventName,
  eventWebsite: showFormState.eventWebsite,
  publication: showFormState.publicationStatus,
  costCenterId: showFormState.costCenter?.costCenterId,
  projectCode: showFormState.projectCode,
  ownerUserId: showFormState.owner?.userId,
  artistId: showFormState.artist?.artistId,
  attendees: showFormState.attendees.map(a => a.contactId),
  venueCompanyId: showFormState.venue?.venueId,
  venueContactId: showFormState.venueContact?.contactId,
  capacity: showFormState.capacity,
  ageRestriction: showFormState.ageRestriction,
  doorsOpen: showFormState.doors.open?.format("HH:mm"),
  doorsClose: showFormState.doors.close?.format("HH:mm"),
  promoterCompanyId: showFormState.promoter?.companyId,
  promoterContactId: showFormState.promoterContact?.contactId,
  hotelCompanyId: showFormState.hotel?.companyId,
  hotelContactId: showFormState.hotelContact?.contactId,
  tickets: {
    // @ts-ignore
    onSaleDate: showFormState.onSaleDate?.toDate() ?? null,
    soldOut: showFormState.soldOut,
    // @ts-ignore
    doorPrice: showFormState.doorPrice,
    // @ts-ignore
    presalePrice: showFormState.presalePrice,
    // @ts-ignore
    reducedPrice: showFormState.reducedPrice,
    ticketSalesWebsite: showFormState.ticketLink
  },
  bookkeepingId: showFormState.bookkeeping.bookkeepingId,
  exchangeRate: showFormState.exchangeRate,
  currency: showFormState.currency,
  productionId: production?.productionId,
  freeFields: showFormState.freefieldValues,
  tagIds: showFormState.tags.map(t => t.tagId),
  validationStatus: "validated"
})

export const mapExternalShowFormStateToInsertShowExternalInstruction = (
  showFormState: ExternalShowFormState
): server.dto.InsertShowExternalEnvironmentInstruction => ({
  dateTime: {
    startDisplay: showFormState.showDates.startDisplayDate.toDate(),
    endDisplay: showFormState.showDates.endDisplayDate.toDate(),
    customFallbackTime: showFormState.showDates.timeOfPerformance,
    mode: showFormState.showDates.mode
  },
  statusId: showFormState.status.statusId,
  eventName: showFormState.eventName,
  eventWebsite: showFormState.eventWebsite,
  freeFields: showFormState.freefieldValues,
  artistId: showFormState.artist.artistId,
  productionId: showFormState.tour?.productionId
})

// TODO: ShowID should be included here, we should implement it when we do the Show Edit page
//  Also, it makes sense to include the freefieldState, bookkeepingCurrency, and production in the ShowFormState
export const mapShowFormStateToUpdateShowInternalInstruction = (
  showFormState: ShowFormState,
  production: server.dto.TourSmall
): Omit<server.dto.UpdateShowInternalEnvironmentInstruction, "showId"> => ({
  dateTime: {
    startDisplay: showFormState.showDates.startDisplayDate.toDate(),
    endDisplay: showFormState.showDates.endDisplayDate.toDate(),
    customFallbackTime: showFormState.showDates.timeOfPerformance,
    mode: showFormState.showDates.mode
  },
  statusId: showFormState.status.statusId,
  eventName: showFormState.eventName,
  eventWebsite: showFormState.eventWebsite,
  publicationStatus: showFormState.publicationStatus,
  costCenterId: showFormState.costCenter?.costCenterId,
  projectCode: showFormState.projectCode,
  ownerUserId: showFormState.owner?.userId,
  artistId: showFormState.artist?.artistId,
  attendees: showFormState.attendees.map(a => a.contactId),
  venueCompanyId: showFormState.venue?.venueId,
  venueContactId: showFormState.venueContact?.contactId,
  capacity: showFormState.capacity,
  ageRestriction: showFormState.ageRestriction,
  doors: {
    open: showFormState.doors.open?.format("HH:mm") ?? null,
    close: showFormState.doors.close?.format("HH:mm") ?? null,
  },
  promoterCompanyId: showFormState.promoter?.companyId,
  promoterContactId: showFormState.promoterContact?.contactId,
  hotelCompanyId: showFormState.hotel?.companyId,
  hotelContactId: showFormState.hotelContact?.contactId,
  tickets: {
    // @ts-ignore
    onSaleDate: showFormState.onSaleDate?.toDate() ?? null,
    soldOut: showFormState.soldOut,
    // @ts-ignore
    doorPrice: showFormState.doorPrice,
    // @ts-ignore
    presalePrice: showFormState.presalePrice,
    // @ts-ignore
    reducedPrice: showFormState.reducedPrice,
    ticketSalesWebsite: showFormState.ticketLink
  },
  bookkeepingId: showFormState.bookkeeping.bookkeepingId,
  exchangeRate: showFormState.exchangeRate,
  currency: showFormState.currency,
  productionId: production?.productionId
})

export const getInstructionForInternal = (
  showId: number,
  showFormState: ShowFormState,
  partial?: Partial<Omit<server.dto.UpdateShowInternalEnvironmentInstruction, "showId">>
): server.dto.UpdateShowInternalEnvironmentInstruction => ({
  showId,
  ...mapShowFormStateToUpdateShowInternalInstruction(showFormState, showFormState.tour),
  ...partial,
});

export const getInstructionForExternal = (
  showId: number,
  showFormState: ShowFormState,
  partial?: Partial<Omit<server.dto.UpdateShowExternalEnvironmentInstruction, "showId">>
): server.dto.UpdateShowExternalEnvironmentInstruction => ({
  showId,
  dateTime: {
    startDisplay: showFormState.showDates.startDisplayDate.toDate(),
    endDisplay: showFormState.showDates.endDisplayDate.toDate(),
    customFallbackTime: showFormState.showDates.timeOfPerformance,
    mode: showFormState.showDates.mode,
  },
  eventName: showFormState.eventName,
  eventWebsite: showFormState.eventWebsite,
  productionId: showFormState.tour?.productionId,
  statusId: showFormState.status?.statusId,
  ...partial,
});

export interface ShowFieldMetaData {
    notValidated: FieldMetaData;

    tour: FieldMetaData;
    artist: FieldMetaData;
    attendeeSection: FieldMetaData;
    bookkeeping: FieldMetaData;    
    currency: FieldMetaData;
    currencyAndExchangeRate: FieldMetaData;
    dateTime: FieldMetaData;
    eventName: FieldMetaData;
    eventWebsite: FieldMetaData;
    exchangeRate: FieldMetaData;
    hotelCompany: FieldMetaData;
    hotelContact: FieldMetaData;
    costCenter: FieldMetaData;
    projectCode: FieldMetaData;
    owner: FieldMetaData;    
    publicationStatus: FieldMetaData;
    status: FieldMetaData;
    tags: FieldMetaData;
    ticketLink: FieldMetaData;
    doorPrice: FieldMetaData;
    presalePrice: FieldMetaData;
    reducedPrice: FieldMetaData;
    onSaleDate: FieldMetaData;
    soldOut: FieldMetaData;
    venueCompany: FieldMetaData;
    venueContact: FieldMetaData;
    ageRestriction: FieldMetaData;
    doors: FieldMetaData;
    capacity: FieldMetaData;
    promoterCompany: FieldMetaData;
    promoterContact: FieldMetaData;
}

export const getShowFieldMetaData = (
    environment: server.dto.SelectedEnvironment,
    userPermissions: server.dto.Permissions,
    item: ShowFormState,
    itemPermissions: server.dto.ShowObjectPermissions,
    validationStatus: server.dto.ShowValidationStatuses,
    hasTags: boolean,
): ShowFieldMetaData => ({
    notValidated: createFieldMetaData(
        false, //Cannot be empty
        environment.type !== "internal" || validationStatus !== "notValidatedInputByExternal",
        false, //Always editable
    ),

    tour: createFieldMetaData(
        !item.tour,
        !environment.tour.enabled || (!item.tour && !itemPermissions.canModify), //SUS to check user-global permissions instead of permission that expresses specific user-to-client-specific permission. Refactor when we work on multi tenant permissions.
        !itemPermissions.canModify,
    ),

    artist: createFieldMetaData(
        !item.artist, 
        !environment.functionGroups.artist.enabled || (!item.artist && !itemPermissions.canModify),  //Hide when empty and read-only
        environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    attendeeSection: createFieldMetaData(
        !item.attendees || item.attendees.length === 0,
        !environment.functionGroups.artist.enabled || !item.artist || ((!item.attendees || item.attendees.length === 0) && !itemPermissions.canModify), //Hide if user cannot read attendees or when empty and read-only
        !itemPermissions.canModify,
    ),

    bookkeeping: createFieldMetaData(
        false, //Cannot be empty
        environment.type === "external", //Hide for external users
        environment.type === "external" || !itemPermissions.canModify,
    ),

    currency: createFieldMetaData(
        false, //Cannot be empty
        environment.type === "external", //Hide for external users
        environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    // The picker in the web app works differently from the mobile, so both the exchange rate and currency fall under one field
    currencyAndExchangeRate: createFieldMetaData(
      false, //Cannot be empty
      environment.type === "external", //Hide for external users
      environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    dateTime: createFieldMetaData(
        false, //Cannot be empty 
        false, //Never hidden
        !itemPermissions.canModify,
    ),

    eventName: createFieldMetaData(
        !item.eventName,
        !item.eventName && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    eventWebsite: createFieldMetaData(
        !item.eventWebsite,
        !item.eventWebsite && !itemPermissions.canModify,
        !itemPermissions.canModify,
    ),

    exchangeRate: createFieldMetaData(
        false, //Cannot be empty
        item.currency === item.bookkeeping.baseCurrency || environment.type === "external", //Hide for external users
        environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    hotelCompany: createFieldMetaData(
        !item.hotel,
        !environment.functionGroups.hotel.enabled || (!item.hotel && !itemPermissions.canModify),
        environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    hotelContact: createFieldMetaData(
        !item.hotelContact,
        !environment.functionGroups.hotel.enabled || !item.hotel || (!item.hotelContact && !itemPermissions.canModify),
        environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    costCenter: createFieldMetaData(
        !item.costCenter,
        !environment.hasCostCenters,
        environment.type === "external"
    ),

    projectCode: createFieldMetaData(
        !item.projectCode,
        environment.projectCode === "hidden" || environment.type === "external",
        environment.type === "external" || !itemPermissions.canModify,
    ),

    owner: createFieldMetaData(
        false, //Cannot be empty
        false, //Never hidden
        environment.type === "external" || !itemPermissions.canModify,
    ),

    publicationStatus: createFieldMetaData(
        false, //Never empty
        false, //Never hidden
        environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    status: createFieldMetaData(
        false, //status is never empty 
        false, //status is never hidden
        !itemPermissions.canModify,
    ),

    tags : createFieldMetaData(
        !item.tags || item.tags.length === 0,
        !hasTags || !itemPermissions.canReadTags || ((!item.tags || item.tags.length === 0) && !itemPermissions.canModify), //Hide if user cannot read tags or when empty and read-only. Also hide when there are no tags in the system.
        environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    ticketLink: createFieldMetaData(
      !item.ticketLink,
      !item.ticketLink && !itemPermissions.canModify,
      environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    doorPrice: createFieldMetaData(
      !item.doorPrice,
      !item.doorPrice && !itemPermissions.canModify,
      environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    presalePrice: createFieldMetaData(
      !item.presalePrice,
      !item.presalePrice && !itemPermissions.canModify,
      environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    reducedPrice: createFieldMetaData(
      !item.reducedPrice,
      !item.reducedPrice && !itemPermissions.canModify,
      environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    onSaleDate: createFieldMetaData(
      !item.onSaleDate,
      !item.onSaleDate && !itemPermissions.canModify,
      environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    soldOut: createFieldMetaData(
      false,
      false,
      environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),
    
    venueCompany: createFieldMetaData(
        !item.venue,
        !environment.functionGroups.venue.enabled || (!item.venue && !itemPermissions.canModify), 
        environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    venueContact: createFieldMetaData(
        !item.venueContact,
        !environment.functionGroups.venue.enabled || !item.venue || (!item.venueContact && !itemPermissions.canModify), 
        environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    ageRestriction: createFieldMetaData(
        item.ageRestriction === "unknown",
        !environment.functionGroups.venue.enabled || !item.venue || (item.ageRestriction === "unknown" && !itemPermissions.canModify), //Hide when no venue or when empty and read-only
        environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    doors: createFieldMetaData(
        !item.doors.open && !item.doors.close, 
        !environment.functionGroups.venue.enabled || !item.venue || (!item.doors.open && !item.doors.close && !itemPermissions.canModify), //Hide when no venue or when empty and read-only
        environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    capacity: createFieldMetaData(
        item.capacity === 0,
        !environment.functionGroups.venue.enabled || !item.venue || (item.capacity === 0 && !itemPermissions.canModify), //Hide when no venue or when empty and read-only
        environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    promoterCompany: createFieldMetaData(
        !item.promoter,
        !environment.functionGroups.promoter.enabled || !itemPermissions.canReadPromoterDetails || (!item.promoter && !itemPermissions.canModify), 
        environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),

    promoterContact: createFieldMetaData(
        !item.promoterContact,
        !environment.functionGroups.promoter.enabled || !itemPermissions.canReadPromoterDetails || !item.promoter || (!item.promoterContact && !itemPermissions.canModify), 
        environment.type === "external" || !itemPermissions.canModify, //External users cannot modify, even with modify permission
    ),
});